import { Alert, App as AntApp, Button, Drawer, Form, Grid, Input, Select, Space, Spin, Typography } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError, errorLines } from "../app/problems.ts";
import { reachOf, teacherMessageKeys, useTeacherMessageOptions } from "../app/teacherMessages.ts";

interface ComposerForm {
  classId: string;
  studentId?: string;
  text: string;
}

interface Props {
  open: boolean;
  onClose(): void;
}

/**
 * A teacher's message to the parents of a class they teach today, or of one student in it (bd-3qg.12). The server decides
 * which classes the teacher may choose; the reach shown comes from the same data. Requires teacherMessages.send.
 */
export function TeacherMessageComposerDrawer({ open, onClose }: Props) {
  const { t } = useTranslation();
  const { modal, message } = AntApp.useApp();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ComposerForm>();
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  // A retried "send" of the same message reuses the key, so it is never sent twice. The drawer stays mounted between
  // opens, so any edit or a successful send starts a new key: otherwise the next message would replay the first one.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const renewIdempotencyKey = () => setIdempotencyKey(crypto.randomUUID());
  const options = useTeacherMessageOptions(open);

  const classId = Form.useWatch("classId", form);
  const studentId = Form.useWatch("studentId", form);
  const schoolClass = options.data?.classes.find((c) => c.classId === classId);
  const reach = schoolClass ? reachOf(schoolClass, studentId) : undefined;
  const maxLength = Number(options.data?.textMaxLength ?? 0) || undefined;

  const mutation = useMutation({
    mutationFn: async (values: ComposerForm) => {
      const { data, error, response } = await api.POST("/teacher-messages", {
        body: { idempotencyKey, classId: values.classId, studentId: values.studentId ?? null, text: values.text.trim() },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (sent) => {
      renewIdempotencyKey();
      await queryClient.invalidateQueries({ queryKey: teacherMessageKeys.all });
      void message.success(t("teacherMessages.composer.sent", { count: Number(sent.queuedCount) }));
      onClose();
    },
    onError: (err) => setServerErrors(errorLines(err, t("app.error"))),
  });

  function confirmAndSend(values: ComposerForm) {
    modal.confirm({
      title: t("teacherMessages.composer.confirmTitle"),
      content: t("teacherMessages.composer.confirmCount", { count: reach?.chats ?? 0 }),
      okText: t("teacherMessages.composer.send"),
      cancelText: t("common.cancel"),
      onOk: () => mutation.mutate(values),
    });
  }

  const notTeacher = options.data === null;
  const noClasses = options.data !== undefined && options.data !== null && options.data.classes.length === 0;
  return (
    <Drawer
      title={t("teacherMessages.composer.title")}
      open={open}
      onClose={onClose}
      size={screens.md ? 560 : "100%"}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="primary" loading={mutation.isPending} disabled={!options.data || noClasses} onClick={() => form.submit()}>
            {t("teacherMessages.composer.send")}
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary">{t("teacherMessages.composer.hint")}</Typography.Paragraph>
      {serverErrors.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} role="alert" title={serverErrors.map((m) => <div key={m}>{m}</div>)} />
      )}
      {options.isPending && <Spin description={t("app.loading")} />}
      {options.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void options.refetch()}>{t("app.retry")}</Button>} />}
      {notTeacher && <Alert type="info" showIcon title={t("teacherMessages.composer.notTeacher")} />}
      {noClasses && <Alert type="info" showIcon title={t("teacherMessages.composer.noClasses")} />}
      {options.data && !noClasses && (
        <Form<ComposerForm> form={form} layout="vertical" requiredMark={false} onFinish={confirmAndSend} onValuesChange={renewIdempotencyKey}>
          <Form.Item name="classId" label={t("teacherMessages.composer.classLabel")} rules={[{ required: true, message: t("teacherMessages.composer.classRequired") }]}>
            <Select
              options={options.data.classes.map((c) => ({ value: c.classId, label: c.className }))}
              onChange={() => form.setFieldValue("studentId", undefined)}
            />
          </Form.Item>
          <Form.Item name="studentId" label={t("teacherMessages.composer.recipientLabel")}>
            <Select
              allowClear
              disabled={!schoolClass}
              placeholder={t("teacherMessages.composer.wholeClass")}
              showSearch={{ optionFilterProp: "label" }}
              options={(schoolClass?.students ?? []).map((s) => ({ value: s.studentId, label: s.fullName }))}
            />
          </Form.Item>
          {reach && (
            <Alert
              type={reach.chats === 0 ? "warning" : "info"}
              showIcon
              style={{ marginBottom: 16 }}
              title={t("teacherMessages.composer.reach", { count: reach.chats })}
              description={reach.notLinked > 0 ? t("teacherMessages.composer.notLinked", { count: reach.notLinked }) : undefined}
            />
          )}
          <Form.Item name="text" label={t("teacherMessages.composer.text")} rules={[{ required: true, whitespace: true, message: t("teacherMessages.composer.textRequired") }]}>
            {/* The character count sits below the box; the margin keeps it clear of the next field. */}
            <Input.TextArea maxLength={maxLength} showCount autoSize={{ minRows: 5, maxRows: 14 }} style={{ marginBottom: 24 }} />
          </Form.Item>
        </Form>
      )}
    </Drawer>
  );
}
